<script setup>
import {
  ref, onMounted, onBeforeUnmount, computed, watch, nextTick,
} from "vue";
import { ElMessage } from "element-plus";
import dayjs from "dayjs";
import { useWindowSize } from "@vueuse/core";
import MovieItem from "@/components/movieItem/index.vue";
import {
  getSeasonsService,
  getAllMovieOpenListService,
  getMovieReviewService,
} from "@/api/dp/index.js";
import {
  getSeaSonByNameAndYear,
  getWeekRanges,
  getWeekIndex,
  getWeekRangeText,
} from "@/utils/movie";
import ResponsiveDateRangePicker from "@/components/ResponsiveDateRangePicker/index.vue";

const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768);

// 每批加载条数（懒加载）
const PAGE_SIZE = 20;

// 排序选项：默认按評価総数（Votes）降序
const SORT_OPTIONS = [
  { label: "評価総数", value: "reviewCount" },
  { label: "評価スコア", value: "rating" },
  { label: "みたい！数", value: "wantToSee" },
  { label: "みた！数", value: "haveSeen" },
  { label: "作品名", value: "title" },
  { label: "配給会社", value: "distributor" },
];
const sortField = ref("reviewCount");
const sortOrder = ref("desc");

const seasonList = ref([]);
const availableYears = ref([]);
const availSeasons = ref([]);
const type = ref("day");
const groupType = ref({
  day: "",
  season: {
    yearVal: "",
    seasonVal: "",
  },
  week: [],
  month: "",
  year: "",
  movieTitle: "",
});

// 列表数据（各视图共用一份扁平列表，週表示时再按周分组展示）
const movieList = ref([]);
// 评价数据：movieId -> comment，懒加载时按批次合并而非整体替换
const commentMap = ref({});
const total = ref(0);
const page = ref(0);
const initialLoading = ref(false);
const loadingMore = ref(false);
const loadError = ref(false);
const seasonLoading = ref(false);
// 并发守卫：筛选条件变化后，丢弃先前请求的返回结果
let requestSeq = 0;

const loadMoreRef = ref(null);
let observer = null;

const disabledDate = (time) => !availableYears.value.includes(time.getFullYear());

// 是否已选择足够的筛选条件（未选择时不发请求）
const hasQueryCondition = computed(() => {
  if (type.value === "season") {
    return !!(groupType.value.season.yearVal && groupType.value.season.seasonVal);
  }
  if (type.value === "week") {
    return Array.isArray(groupType.value.week) && groupType.value.week.length === 2;
  }
  return !!groupType.value[type.value];
});

// 已全部加载完毕
const isFinished = computed(
  () => movieList.value.length > 0 && movieList.value.length >= total.value,
);

// 还能继续加载下一批
const canLoadMore = computed(
  () => hasQueryCondition.value
    && !initialLoading.value
    && !loadingMore.value
    && !loadError.value
    && movieList.value.length > 0
    && movieList.value.length < total.value,
);

// 週表示：按周区间分组，懒加载追加的数据会自动归入对应周
const weekGroups = computed(() => {
  if (type.value !== "week" || !hasQueryCondition.value) return [];
  const ranges = getWeekRanges(groupType.value.week);
  const grouped = new Map();
  movieList.value.forEach((item) => {
    const weekIndex = getWeekIndex(item.openDate, ranges);
    if (weekIndex === -1) return;
    const key = ranges[weekIndex];
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(item);
  });
  return Array.from(grouped.entries()).map(([range, items]) => ({ range, items }));
});

/**
 * 按当前视图构造查询区间；条件不足时返回 null
 */
const buildQueryParams = () => {
  if (type.value === "day") {
    const { day } = groupType.value;
    if (!day) return null;
    return {
      startTime: dayjs(day).startOf("day").valueOf(),
      endTime: dayjs(day).startOf("day").add(1, "day").valueOf(),
    };
  }
  if (type.value === "week") {
    const week = groupType.value.week;
    if (!Array.isArray(week) || week.length !== 2) return null;
    return {
      startTime: dayjs(week[0]).startOf("day").valueOf(),
      endTime: dayjs(week[1]).add(1, "day").startOf("day").valueOf(),
    };
  }
  if (type.value === "month") {
    const { month } = groupType.value;
    if (!month) return null;
    return {
      startTime: dayjs(month).startOf("month").valueOf(),
      endTime: dayjs(month).endOf("month").add(1, "day").startOf("day")
        .valueOf(),
    };
  }
  if (type.value === "year") {
    const { year } = groupType.value;
    if (!year) return null;
    return {
      startTime: dayjs(year).startOf("year").valueOf(),
      endTime: dayjs(year).endOf("year").add(1, "day").startOf("day")
        .valueOf(),
    };
  }
  if (type.value === "season") {
    const { yearVal, seasonVal } = groupType.value.season;
    if (!yearVal || !seasonVal) return null;
    const curSeason = getSeaSonByNameAndYear(availSeasons.value, seasonVal, yearVal);
    if (!curSeason) {
      ElMessage.error("季節が見つかりません");
      return null;
    }
    return {
      startTime: dayjs(curSeason.startTime).startOf("day").valueOf(),
      endTime: dayjs(curSeason.endTime).add(1, "day").startOf("day").valueOf(),
      seasonRanges: [curSeason],
    };
  }
  return null;
};

/**
 * 拉取本批作品的评价数据并合并进 commentMap（失败不影响列表展示）
 */
const fetchComments = (rows, seq) => {
  const movieIds = rows.map((item) => item.movieId).filter((id) => id !== undefined && id !== null);
  if (movieIds.length === 0) return Promise.resolve();
  return getMovieReviewService({ movieId: movieIds.join(",") })
    .then((res) => {
      if (seq !== requestSeq || !Array.isArray(res)) return;
      const merged = { ...commentMap.value };
      res.forEach((comment) => {
        merged[comment.movieId] = comment;
      });
      commentMap.value = merged;
    })
    .catch(() => {
      // 评价数据获取失败时保留列表，评分区域按空值显示
    });
};

const clearList = () => {
  requestSeq += 1;
  movieList.value = [];
  commentMap.value = {};
  total.value = 0;
  page.value = 0;
  loadError.value = false;
  initialLoading.value = false;
  loadingMore.value = false;
};

/**
 * 加载列表
 * @param {boolean} reset true=重新从第一批加载（切换筛选条件/排序时），false=追加下一批
 */
const fetchList = async (reset = false) => {
  const params = buildQueryParams();
  if (!params) {
    clearList();
    return;
  }
  if (reset) {
    requestSeq += 1;
    movieList.value = [];
    commentMap.value = {};
    total.value = 0;
    page.value = 0;
  }
  const seq = reset ? requestSeq : (requestSeq += 1);
  const nextPage = page.value + 1;
  loadError.value = false;
  if (reset) {
    initialLoading.value = true;
  } else {
    loadingMore.value = true;
  }
  try {
    const res = await getAllMovieOpenListService({
      ...params,
      groupType: type.value,
      page: nextPage,
      pageSize: PAGE_SIZE,
      sortField: sortField.value,
      sortOrder: sortOrder.value,
    });
    if (seq !== requestSeq) return;
    const rows = Array.isArray(res.rows) ? res.rows : [];
    // 批次之间去重，避免同一作品重复出现
    const existedIds = new Set(movieList.value.map((item) => item.movieId));
    const freshRows = rows.filter((item) => !existedIds.has(item.movieId));
    movieList.value = movieList.value.concat(freshRows);
    total.value = res.total || 0;
    page.value = nextPage;
    await fetchComments(rows, seq);
  } catch (error) {
    if (seq !== requestSeq) return;
    // 请求失败时保持已加载内容，由用户点击「再試行」重新加载该批次
    loadError.value = true;
  } finally {
    if (seq === requestSeq) {
      initialLoading.value = false;
      loadingMore.value = false;
      nextTick(scheduleAutoLoad);
    }
  }
};

const loadMore = () => {
  if (!canLoadMore.value) return;
  fetchList(false);
};

/**
 * 一批加载完成后，若哨兵仍留在视口内（例如首批未撑满一屏），继续加载下一批。
 * IntersectionObserver 只在相交状态发生变化时回调，这里做补充触发。
 */
const scheduleAutoLoad = () => {
  const el = loadMoreRef.value;
  if (!el || !canLoadMore.value) return;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  if (el.getBoundingClientRect().top <= viewportHeight + 200) {
    loadMore();
  }
};

const retry = () => {
  loadError.value = false;
  // 首批失败则重新加载首批，否则重新加载失败的那一批
  fetchList(movieList.value.length === 0);
};

const reload = () => {
  if (!hasQueryCondition.value) {
    clearList();
    return;
  }
  fetchList(true);
};

const getSeasonsByYear = (year) => {
  groupType.value.season.seasonVal = "";
  seasonList.value = [];
  clearList();
  const curAvailSeasons = availSeasons.value.find((item) => item.year === Number(year));
  if (curAvailSeasons) {
    seasonList.value = curAvailSeasons.ranges;
  }
};

const changeType = () => {
  groupType.value = {
    day: "",
    season: {
      yearVal: "",
      seasonVal: "",
    },
    week: [],
    month: "",
    year: "",
    movieTitle: "",
  };
  seasonList.value = [];
  clearList();
};

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === "desc" ? "asc" : "desc";
};

// 排序条件变化后重置列表并从第一批重新加载
watch([sortField, sortOrder], () => {
  if (!hasQueryCondition.value) return;
  reload();
});

onMounted(() => {
  seasonLoading.value = true;
  getSeasonsService()
    .then((res) => {
      availableYears.value = res.map((item) => item.year);
      availSeasons.value = res;
    })
    .finally(() => {
      seasonLoading.value = false;
    });

  // 滚动到列表底部附近时自动加载下一批
  nextTick(() => {
    if (!loadMoreRef.value || typeof IntersectionObserver === "undefined") return;
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(loadMoreRef.value);
  });
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
</script>

<template>
  <div class="page-container">
    <!-- 工具栏：视图切换 / 期間選択 / 並び替え -->
    <div class="toolbar">
      <h1 class="title">作品カレンダー</h1>
      <div class="toolbar-controls">
        <div class="control-field date-field">
          <template v-if="type === 'season'">
            <el-date-picker
              v-model="groupType.season.yearVal"
              class="control-item year-picker"
              type="year"
              placeholder="年を選択してください"
              format="YYYY年"
              value-format="YYYY"
              :disabled-date="disabledDate"
              @change="(val) => getSeasonsByYear(val)"
            />
            <el-select
              v-model="groupType.season.seasonVal"
              class="control-item"
              placeholder="休日を選択してください"
              :loading="seasonLoading"
              @change="reload"
            >
              <el-option v-for="(item, idx) in seasonList" :key="idx" :label="item.name" :value="item.name" />
            </el-select>
          </template>
          <el-date-picker
            v-else-if="type === 'day'"
            v-model="groupType.day"
            class="control-item"
            type="date"
            placeholder="日付を選択する"
            @change="reload"
          />
          <ResponsiveDateRangePicker
            v-else-if="type === 'week' && isMobile"
            v-model="groupType.week"
            class="control-item"
            start-placeholder="開始日"
            end-placeholder="終了日"
            placeholder="期間を選択"
            :default-value="[new Date(), new Date()]"
            @change="reload"
          />
          <el-date-picker
            v-else-if="type === 'week'"
            v-model="groupType.week"
            class="control-item"
            type="daterange"
            start-placeholder="開始日"
            end-placeholder="終了日"
            @change="reload"
          />
          <el-date-picker
            v-else-if="type === 'month'"
            v-model="groupType.month"
            class="control-item"
            type="month"
            placeholder="月を選択してください"
            @change="reload"
          />
          <el-date-picker
            v-else-if="type === 'year'"
            v-model="groupType.year"
            class="control-item"
            type="year"
            placeholder="年を選択してください"
            @change="reload"
          />
        </div>

        <div class="control-field sort-field">
          <el-select v-model="sortField" class="control-item sort-select" placeholder="並び替え">
            <el-option
              v-for="option in SORT_OPTIONS"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <button
            type="button"
            class="sort-order-btn"
            :title="sortOrder === 'desc' ? '降順' : '昇順'"
            @click="toggleSortOrder"
          >
            <i :class="sortOrder === 'desc' ? 'fas fa-arrow-down-wide-short' : 'fas fa-arrow-up-short-wide'" />
            <span>{{ sortOrder === "desc" ? "降順" : "昇順" }}</span>
          </button>
        </div>

        <el-select v-model="type" class="control-item type-select" placeholder="Pick a type" @change="changeType">
          <el-option label="日" value="day" />
          <el-option label="イベント" value="season" />
          <el-option label="週" value="week" />
          <el-option label="月" value="month" />
          <el-option label="年" value="year" />
        </el-select>
      </div>
    </div>

    <!-- 選択中の期間 -->
    <p v-if="type === 'season' && groupType.season.yearVal && groupType.season.seasonVal" class="list-title">
      {{ groupType.season.yearVal }}<span>年</span> {{ groupType.season.seasonVal }}
    </p>
    <p v-else-if="type === 'day' && groupType.day" class="list-title">
      {{ dayjs(groupType.day).format("YYYY") }}<span>年</span>
      {{ dayjs(groupType.day).format("MM") }}<span>月</span>
      {{ dayjs(groupType.day).format("DD") }}<span>日</span>
    </p>
    <p v-else-if="type === 'month' && groupType.month" class="list-title">
      {{ dayjs(groupType.month).format("YYYY") }}<span>年</span>
      {{ dayjs(groupType.month).format("MM") }}<span>月</span>
    </p>
    <p v-else-if="type === 'year' && groupType.year" class="list-title">
      {{ dayjs(groupType.year).format("YYYY") }}<span>年</span>
    </p>

    <hr v-if="type !== 'week'" />

    <!-- 週表示：按周分组 -->
    <template v-if="type === 'week'">
      <div v-for="group in weekGroups" :key="group.range" class="week-group">
        <p class="list-title">
          {{ getWeekRangeText(group.range).start.year }}<span>年</span>
          {{ getWeekRangeText(group.range).start.month }}<span>月</span>
          {{ getWeekRangeText(group.range).start.day }}<span>日</span> ~
          {{ getWeekRangeText(group.range).end.year }}<span>年</span>
          {{ getWeekRangeText(group.range).end.month }}<span>月</span>
          {{ getWeekRangeText(group.range).end.day }}<span>日</span>
        </p>
        <hr />
        <div class="movie-list-container">
          <MovieItem
            v-for="item in group.items"
            :key="item.movieId"
            :item="item"
            :comment="commentMap[item.movieId]"
          />
        </div>
      </div>
    </template>
    <!-- その他の表示 -->
    <div v-else-if="movieList.length > 0" class="movie-list-container">
      <MovieItem
        v-for="item in movieList"
        :key="item.movieId"
        :item="item"
        :comment="commentMap[item.movieId]"
      />
    </div>

    <!-- 状態表示：初回読み込み / 追加読み込み / 失敗 / 完了 / 該当なし -->
    <div class="list-status">
      <div v-if="initialLoading" class="status-block status-loading">
        <i class="fas fa-circle-notch fa-spin" />
        <span>読み込み中...</span>
      </div>
      <div v-else-if="loadError" class="status-block status-error">
        <span>読み込みに失敗しました。</span>
        <button type="button" class="retry-btn" @click="retry">
          <i class="fas fa-rotate-right" />
          <span>再試行</span>
        </button>
      </div>
      <template v-else-if="!hasQueryCondition">
        <div class="status-block status-empty">
          <i class="fas fa-calendar-days" />
          <span>期間を選択すると作品が表示されます。</span>
        </div>
      </template>
      <template v-else>
        <div v-if="loadingMore" class="status-block status-loading">
          <i class="fas fa-circle-notch fa-spin" />
          <span>読み込み中...</span>
        </div>
        <div v-else-if="movieList.length === 0" class="status-block status-empty">
          <i class="fas fa-film" />
          <span>該当する作品がありません。</span>
        </div>
        <div v-else-if="isFinished" class="status-block status-finished">
          <span>すべて表示しました（全 {{ total }} 件）</span>
        </div>
      </template>
      <!-- 懒加载哨兵：滚动到此处时自动加载下一批 -->
      <div ref="loadMoreRef" class="load-more-sentinel" />
    </div>
  </div>
</template>

<style lang="less" scoped>
.page-container {
  padding: 15px;
  padding-right: 24px;
  box-sizing: border-box;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  .title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: var(--text-primary);
    white-space: nowrap;
    flex-shrink: 0;
  }
}

.toolbar-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.control-field {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-item {
  width: 180px;
}

.year-picker {
  width: 150px;
}

.sort-select {
  width: 150px;
}

.type-select {
  width: 120px;
}

/* 昇順/降順の切り替え */
.sort-order-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius, 6px);
  background: var(--bg-panel);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;

  &:hover {
    color: var(--brand-color);
    border-color: color-mix(in srgb, var(--brand-color) 55%, var(--border-color));
  }

  i {
    font-size: 12px;
  }
}

.list-title {
  margin: 23px 0 0;
  padding: 0;
  font-size: 32px;
  font-weight: 700;
  color: var(--brand-color);

  span {
    font-size: 12px;
    font-weight: 400;
    color: var(--text-secondary);
  }
}

hr {
  height: 2px;
  margin: 23px 0;
  border: none;
  background-color: var(--brand-color);
}

.movie-list-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.week-group + .week-group {
  margin-top: 8px;
}

/* 状態表示 */
.list-status {
  margin-top: 20px;
}

.status-block {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px 12px;
  color: var(--text-secondary);
  font-size: 14px;

  i {
    color: var(--brand-color);
  }
}

/* 読み込み中：文字色も「みたい！/みた！」の件数と同じブランドカラーに揃える */
.status-loading {
  color: var(--brand-color);
}

.status-empty {
  flex-direction: column;
  gap: 12px;
  color: var(--text-tertiary, var(--text-secondary));

  i {
    font-size: 22px;
    opacity: 0.6;
  }
}

.status-finished {
  color: var(--text-tertiary, var(--text-secondary));
  font-size: 13px;
}

.status-error {
  color: var(--accent-red);
}

.retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--brand-color);
  border-radius: var(--border-radius, 6px);
  background: transparent;
  color: var(--brand-color);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: color-mix(in srgb, var(--brand-color) 12%, transparent);
  }
}

.load-more-sentinel {
  height: 1px;
}

@media (max-width: 768px) {
  .page-container {
    padding: 12px;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .toolbar-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .control-field {
    width: 100%;
  }

  .date-field {
    flex-wrap: wrap;
  }

  .control-item,
  .year-picker,
  .sort-select {
    width: 100%;
    flex: 1 1 auto;
  }

  .sort-order-btn {
    height: 36px;
    flex-shrink: 0;
  }

  .type-select {
    width: 100%;
  }

  .list-title {
    font-size: 24px;
    margin-top: 18px;
  }

  hr {
    margin: 16px 0;
  }
}
</style>
